/**
 * GitHub API utility with rate limiting and error handling
 */

// Simple rate limiter to prevent too many simultaneous API calls
class RateLimiter {
  private queue: (() => Promise<any>)[] = [];
  private processing = false;
  private delay = 100; // 100ms delay between API calls

  async execute<T>(fn: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      this.queue.push(async () => {
        try {
          const result = await fn();
          resolve(result);
        } catch (error) {
          reject(error);
        }
      });
      
      this.processQueue();
    });
  }

  private async processQueue() {
    if (this.processing || this.queue.length === 0) {
      return;
    }

    this.processing = true;

    while (this.queue.length > 0) {
      const nextRequest = this.queue.shift();
      if (nextRequest) {
        await nextRequest();
        // Add delay between requests to avoid rate limiting
        if (this.queue.length > 0) {
          await new Promise(resolve => setTimeout(resolve, this.delay));
        }
      }
    }

    this.processing = false;
  }
}

// Global rate limiter instance
const gitHubRateLimiter = new RateLimiter();

/**
 * Make a rate-limited fetch request to GitHub API with proper error handling
 */
export async function fetchGitHubAPI(url: string, headers: HeadersInit): Promise<Response> {
  return gitHubRateLimiter.execute(async () => {
    const response = await fetch(url, { headers });
    
    // Handle rate limiting with exponential backoff
    if (response.status === 403) {
      const errorData = await response.json().catch(() => ({}));
      if (errorData.message && errorData.message.includes('rate limit')) {
        // Wait and retry once
        await new Promise(resolve => setTimeout(resolve, 2000));
        const retryResponse = await fetch(url, { headers });
        if (retryResponse.status === 403) {
          throw new Error('GitHub API rate limit exceeded. Please try again later.');
        }
        return retryResponse;
      }
    }
    
    return response;
  });
}

/**
 * Safe JSON parsing with error handling
 */
export async function safeJsonParse(response: Response): Promise<any> {
  try {
    return await response.json();
  } catch (error) {
    console.warn('Failed to parse JSON response:', error);
    return {};
  }
}
